/**
 * SessionIFrame class handles refreshing of SSO access token
 *
 * @property {URL} _refreshSsoUrl
 * @property {Logger} _logger
 * @property {string} _idTokenCookieName
 * @property {number} _tokenRefreshInterval
 * @property {HTMLIFrameElement} _frame
 * @property {?number} _refreshIntervalId
 */
export default class SessionIFrame {
  /**
   * @param {string} monicaApiUrl
   * @param {URL} refreshSsoUrl
   * @param {Logger} logger
   * @param {object} config
   */
  constructor(monicaApiUrl, refreshSsoUrl, logger, config) {
    this._monicaApiUrl = monicaApiUrl;
    this._refreshSsoUrl = refreshSsoUrl;
    this._logger = logger;
    this._idTokenCookieName = config.idTokenCookieName;
    this._tokenRefreshInterval = config.iframeRefreshInterval;
    this._iFrame = null;
    this._refreshIntervalId = null;
  }

  /**
   * Create iFrame, immediately use it to attempt to refresh the ID token, if present, and schedule repeated refreshes
   * in specified interval.
   *
   * @param {?string} code
   * @returns {Promise<void>}
   */
  async initialize(code = null) {
    this._initializeIFrame();

    await this._refreshToken(code);

    this._scheduleNextRefresh();
  }

  /**
   * Stops automatic token refresh
   */
  stop() {
    this._unscheduleNextRefresh();
  }

  /**
   * Create and visually hide iFrame, with SSO refresh token link as a source
   */
  _initializeIFrame() {
    const iFrame = document.createElement('iframe');

    iFrame.setAttribute('src', 'about:blank');
    iFrame.style.visibility = 'hidden';
    iFrame.style.position = 'absolute';
    iFrame.style.display = 'none';
    iFrame.style.width = 0;
    iFrame.style.height = 0;

    window.document.body.appendChild(iFrame);
    this._iFrame = iFrame;
  }

  /**
   * If ID token is present attempt to refresh it by visiting the configured SSO URL
   *
   * Token refresh has five steps (all of them have to happen in the iframe so that HTTP-Only cookies can be set):
   *   1) SSO refresh URL is visited, SSO reads current token, and if its recognized its refreshed
   *   2) SSO does one more internal redirect
   *   3) SSO redirects to "monica" API with the `code`
   *   4) "monica" API exchanges the `code` for token with the SSO (on the backend)
   *   5) "monica" returns a page that sets all the new token cookies (access_token and id_token)
   *
   * If we already have the `code` which can be exchanged for a token (e.g. during identity switch journey),
   * we no longer need to do steps 1-3, we can go directly to the token exchange. This journey doesn't require already
   * having tokens.
   *
   * @param {?string} code - code that can be exchanged for tokens
   * @returns {Promise<void>}
   * @private
   */
  _refreshToken(code = null) {
    return new Promise(resolve => {
      if (!this._idTokenCookieExist && !code) {
        this._logger.debug('Token not found, refresh skipped.', 'infra-auth-manager');
        resolve();
        return;
      }

      this._iFrame.addEventListener(
        'load',
        () => {
          this._logger.debug('Token refreshed.', 'infra-auth-manager');
          resolve();
        },
        { once: true },
      );

      // If we already have code we skip directly to the token exchange step
      if (code) {
        this._iFrame.contentWindow.location.replace(`${this._monicaApiUrl}/auth?code=${code}`);
      } else {
        // Otherwise, we go to SSO and follow all of its redirects (which ends with a token exchange)
        this._iFrame.contentWindow.location.replace(this._refreshSsoUrl);
      }
    });
  }

  /**
   * After configured time interval passes, trigger a token refresh, and immediately recursively schedule
   * another refresh
   *
   * @private
   */
  _scheduleNextRefresh() {
    this._unscheduleNextRefresh(); // Reset, in case there's already a refresh scheduled

    this._refreshIntervalId = window.setInterval(
      async () => {
        await this._refreshToken();
        this._scheduleNextRefresh();
      },
      this._tokenRefreshInterval * 1000, // refresh interval is provided in seconds, change to milliseconds
    );
  }

  /**
   * Remove a scheduled refresh
   *
   * @private
   */
  _unscheduleNextRefresh() {
    if (this._refreshIntervalId) {
      window.clearInterval(this._refreshIntervalId);
      this._refreshIntervalId = null;
    }
  }

  /**
   * Check if cookie with specific name exist
   *
   * @returns {Boolean}
   * @private
   */
  get _idTokenCookieExist() {
    return (
      document.cookie
        .split(';')
        .filter(item => item.trim().startsWith(`${this._idTokenCookieName}=`)).length > 0
    );
  }
}
